import { useState, useContext, useEffect, useMemo, useRef } from 'react';
import { Collapse } from 'bootstrap';
import { formatCurrency as currency } from '../../utils/formatCurrency';
import { CartContext } from '../../store/CartContext';
import { getUpsellTotal } from '../../utils/amountHelper';
import OrderSummaryItem from './OrderSummaryItem';
import showFeesInCart from '../../utils/showFeesInCart';

// import CheckoutBtn from './CheckoutBtn';
// import CouponField from '../CouponField';

// ESlint complains about prop-types, you need to define it
import PropTypes from 'prop-types';
OrderSummary.propTypes = {
  showQtyControls: PropTypes.bool,
  showQtyInput: PropTypes.bool,
};

export default function OrderSummary({ showQtyControls = true, showQtyInput = true }) {
  const [cartState, cartDispatch] = useContext(CartContext);

  const isSingle = cartState?.cartProducts?.length > 1 ? false : true;

  const [isSummaryCollapsed, setIsSummaryCollapsed] = useState(true);
  const [collapse, setCollapse] = useState(null);
  const itemsCollapse = useRef();

  useEffect(() => {
    setCollapse(
      new Collapse(itemsCollapse.current, {
        toggle: false,
      })
    );

    // using 'show.bs.collapse' because the effect is immediate
    itemsCollapse.current.addEventListener('show.bs.collapse', () => {
      setIsSummaryCollapsed(false);
    });
    // using 'hidden.bs.collapse' because the effect is after it collapses
    itemsCollapse.current.addEventListener('hide.bs.collapse', () => {
      setIsSummaryCollapsed(true);

      // trigger to expand of order summary
      cartDispatch({
        type: 'EXPERIENCE',
        payload: {
          expandOrderSummary: false,
        },
      });
    });
  }, [cartDispatch]);

  // open the order summary if user clicked view summary from the toast notification
  useEffect(() => {
    if (cartState.userExperience.expandOrderSummary) {
      collapse.show();
    }
  }, [cartState.userExperience.expandOrderSummary, collapse]);

  const toggleCollapse = () => {
    collapse.toggle();
  };

  const appliedCoupons = useMemo(() => {
    const coupons = [];

    cartState.cartProducts?.forEach((item) => {
      const isCouponApplied = item.discountSource === 'Coupon' && item.combinedDiscount > 0;

      if (isCouponApplied) {
        coupons.push(item.discountCode);
      }

      item.upSellProducts
        .filter((item) => item.isSelected)
        .forEach((upsell) => {
          const isCouponApplied = upsell.discountSource === 'Coupon' && upsell.combinedDiscount > 0;

          if (isCouponApplied) {
            coupons.push(upsell.discountCode);
          }
        });
    });

    return [...new Set(coupons)].length ? [...new Set(coupons)].join(', ') : null;
  }, [cartState.cartProducts]);

  const savings = useMemo(
    () =>
      cartState.cartProducts?.reduce((acc, cur) => {
        const upsellSavingsTotal = cur.upSellProducts
          .filter((item) => item.isSelected)
          .reduce((acc2, cur2) => {
            return acc2 + cur2.combinedDiscount * cur2.quantity;
          }, 0);
        return acc + upsellSavingsTotal + cur.combinedDiscount * cur.quantity;
      }, 0),
    [cartState.cartProducts]
  );

  const subTotal = useMemo(
    () =>
      cartState.cartProducts?.reduce((acc, cur) => {
        const upsellTotal = getUpsellTotal(cur.upSellProducts);
        if (showFeesInCart(cur.productId)) {
          return acc + cur.unitPriceWithDiscountAndFee * cur.quantity + upsellTotal;
        }
        return acc + cur.unitPriceWithDiscountAndFeeIfHidden * cur.quantity + upsellTotal;
      }, 0),
    [cartState.cartProducts]
  );

  const cartItemsWithoutReplaceUpsell =
    cartState.cartProducts?.filter(
      (product) =>
        product.upSellProducts.length === 0 ||
        !product.upSellProducts.some((upsell) => {
          return upsell.upsellType === 'Replace';
        })
    ) || [];
  const cartItemsWithReplaceUpsell =
    cartState.cartProducts?.filter((product) => product.upSellProducts.some((upsell) => upsell.upsellType === 'Replace')) ||
    [];

  const sortedItems = cartState.cartProducts ? [...cartItemsWithoutReplaceUpsell, ...cartItemsWithReplaceUpsell] : undefined;

  const cartItems = sortedItems?.map((product) => {
    return (
      <OrderSummaryItem
        item={product}
        isSingle={isSingle}
        key={product.cart_ProductId}
        showQtyControls={showQtyControls}
        showQtyInput={showQtyInput}
      />
    );
  });

  return (
    <>
      {/* <div
        className={`card card-summary sticky-top py-1 mb-3 ${cartState.isLoading ? 'is-updating-cart' : ''}`}
        id="order-summary"
      > */}
      <div className={`card card-summary sticky-top py-1 mb-3`} id="order-summary">
        <div className="card-body p-0">
          <ul className="list-group list-group-flush bg-transparent m-0">
            <li className="list-group-item fw-700 align-items-center fs-6 px-3">
              <div className="item-details w-100">
                <h3 className="fs-6 fw-700 my-1 d-none d-lg-block">Order Summary</h3>
                <button
                  className={`summary-collapse-toggle fs-6 fw-700 text-start my-1 d-lg-none ${
                    !isSummaryCollapsed ? 'is-shown' : ''
                  }`}
                  type="button"
                  onClick={toggleCollapse}
                >
                  Order Summary
                </button>
              </div>
              {isSummaryCollapsed && (
                <div className="item-amount text-end d-lg-none">
                  {cartState.isLoading ? (
                    <span className="animated-placeholder rounded-2 w-70 height-md" style={{ minWidth: '70px' }}></span>
                  ) : (
                    currency(subTotal)
                  )}
                </div>
              )}
            </li>

            {/* <li className="list-group-item fw-700 fs-6 px-0">
              <div className="item-details">Order Summary</div>
              <div className="item-amount text-end d-lg-none">
                {cartState.isLoading ? (
                  <span className="animated-placeholder rounded-2 w-70 height-md" style={{ minWidth: '70px' }}></span>
                ) : (
                  currency(subTotal)
                )}
              </div>
            </li> */}

            {/* {cartItems} */}
            <li className="list-group-item p-0 collapse" ref={itemsCollapse} id={`order-summary-items-collapse`}>
              <div className="order-summary-viewport w-100 d-lg-flex flex-column">
                <ul className="order-summary-scroll list-group list-group-flush bg-transparent w-100 px-3 m-0 overflow-auto">
                  {cartItems}
                </ul>
              </div>
            </li>
          </ul>
          <div className="order-summary-footer position-relative">
            <div
              className={`list-group-item total border-top fw-700 fs-6 px-3 ${
                isSummaryCollapsed ? 'd-none' : 'd-flex'
              } d-lg-flex`}
            >
              <div className="item-details">Total</div>
              <div className="item-amount text-end">
                {cartState.isLoading ? (
                  <span className="animated-placeholder rounded-2 w-70 height-md" style={{ minWidth: '70px' }}></span>
                ) : (
                  currency(subTotal)
                )}
              </div>
            </div>
            {savings > 0 && (
              <div className="list-group-item savings-display border-top">
                <div className="item-details fw-700 text-success ps-3">
                  You Save
                  {appliedCoupons && <div className="coupon-display fw-400">Coupon: {appliedCoupons}</div>}
                </div>
                <div className="item-amount text-end fw-700 text-success pe-3">
                  {cartState.isLoading ? (
                    <span className="animated-placeholder rounded-2 w-70 height-sm" style={{ minWidth: '70px' }}></span>
                  ) : (
                    currency(savings)
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* <CouponField /> */}

      {/* <CheckoutBtn /> */}
    </>
  );
}
